<template>
  <div class="multi-checkbox">
    <label class="multi-checkbox__label" v-if="label">{{ label }}</label>

    <div class="multi-checkbox__list" role="group" :aria-label="label">
      <label
        v-for="option in normalizedOptions"
        :key="option.id"
        class="multi-checkbox__item"
        :class="{ 'multi-checkbox__item--checked': isSelected(option.id) }"
      >
        <input
          type="checkbox"
          class="multi-checkbox__input"
          :value="option.id"
          :checked="isSelected(option.id)"
          :disabled="option.disabled"
          @change="handleChange(option.id, $event)"
        />
        <span class="multi-checkbox__box" aria-hidden="true">
          <svg v-if="isSelected(option.id)" viewBox="0 0 10 8" fill="none">
            <path d="M1 4L3.5 6.5L9 1" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </span>
        <span class="multi-checkbox__text">{{ option.name }}</span>
      </label>
    </div>

    <!-- Mirrors native <select multiple> — hidden inputs for form submission -->
    <template v-if="name">
      <input
        v-for="val in modelValue"
        :key="val"
        type="hidden"
        :name="name"
        :value="val"
      />
    </template>
  </div>
</template>

<script>
  export default {
    name: "MultiCheckbox",
    // props: ['option'],
    props: {
      options: {
        type: Array,
        required: true,
      },
      modelValue: {
        type: Array,
        default: () => [],
      },
      label: {
        type: String,
        default: '',
      },
      name: {
        type: String,
        default: '',
      },
    },
    data() {
      return {
        isSelected: function () {
          return this.props.modelValue.includes(value);
        }
      }
    },
    computed: {
      normalizedOptions: function () {
        return this.props.options.map((opt) =>
                typeof opt === 'object' && opt !== null
                        ? { disabled: false, ...opt }
                        : { label: String(opt), value: opt, disabled: false }
        )
      }
    },
    methods: {
      handleChange: function(value, event) {
        const next = event.target.checked
                ? [...this.props.modelValue, value]
                : this.props.modelValue.filter((v) => v !== value)

        emit('update:modelValue', next)
      }
    }
  }

// import { computed } from 'vue'

/**
 * Props mirror the <select multiple> API as closely as possible.
 *
 * options — Array of:
 *   - strings / numbers  →  { label: x, value: x }
 *   - { label, value }
 *   - { label, value, disabled }
 *
 * modelValue — Array of selected values  (v-model)
 * label      — Accessible group label
 * name       — HTML form field name (for native form submission)
 */
// const props = defineProps({
//   options: {
//     type: Array,
//     required: true,
//   },
//   modelValue: {
//     type: Array,
//     default: () => [],
//   },
//   label: {
//     type: String,
//     default: '',
//   },
//   name: {
//     type: String,
//     default: '',
//   },
// })
//
// const emit = defineEmits(['update:modelValue'])
//
// // Normalise plain strings / numbers into { label, value, disabled } objects
// const normalizedOptions = computed(() =>
//   props.options.map((opt) =>
//     typeof opt === 'object' && opt !== null
//       ? { disabled: false, ...opt }
//       : { label: String(opt), value: opt, disabled: false }
//   )
// )
//
// const isSelected = (value) => props.modelValue.includes(value)
//
// function handleChange(value, event) {
//   const next = event.target.checked
//     ? [...props.modelValue, value]
//     : props.modelValue.filter((v) => v !== value)
//
//   // emit('update:modelValue', next)
//   this.$emit('update:modelValue', next);
// }
</script>

<style scoped>
.multi-checkbox {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-family: inherit;
}

.multi-checkbox__label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 2px;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.multi-checkbox__list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 6px;
  background: #fff;
}

.multi-checkbox__item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.12s ease;
  user-select: none;
  color: #1f2937;
  font-size: 0.95rem;
}

.multi-checkbox__item:hover {
  background: #f3f4f6;
}

.multi-checkbox__item--checked {
  background: #eff6ff;
  color: #1d4ed8;
}

.multi-checkbox__item--checked:hover {
  background: #dbeafe;
}

/* Hide the native input but keep it accessible */
.multi-checkbox__input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  pointer-events: none;
}

/* Custom checkbox box */
.multi-checkbox__box {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  border: 2px solid #d1d5db;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  transition: border-color 0.12s, background 0.12s;
  color: #fff;
}

.multi-checkbox__item--checked .multi-checkbox__box {
  border-color: #3b82f6;
  background: #3b82f6;
}

.multi-checkbox__box svg {
  width: 10px;
  height: 8px;
}

/* Disabled state */
.multi-checkbox__input:disabled ~ .multi-checkbox__box {
  border-color: #e5e7eb;
  background: #f9fafb;
  cursor: not-allowed;
}

.multi-checkbox__input:disabled ~ .multi-checkbox__text {
  color: #9ca3af;
}

/* Keyboard focus ring */
.multi-checkbox__input:focus-visible ~ .multi-checkbox__box {
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
}
</style>
